import { getPermutivePFPSegments } from '@guardian/commercial';
import { loadScript, log, onConsentChange } from '@guardian/libs';
import fastdom from 'fastdom';
import { getPageTargeting } from 'common/modules/commercial/build-page-targeting';
import { commercialFeatures } from 'common/modules/commercial/commercial-features';
import config from 'lib/config';
import { constructQuery } from 'lib/url';
const scriptSrc = 'https://www.youtube.com/iframe_api';
const youtubeReady = new Promise((resolve) => {
    const localWindow = window;
    if (localWindow.YT?.Player) {
        resolve();
    }
    else if (localWindow.onYouTubeIframeAPIReady) {
        // If there’s already a callback registered.
        // This happens with the standalone commercial bundle
        const previousApiLoadCallback = localWindow.onYouTubeIframeAPIReady;
        localWindow.onYouTubeIframeAPIReady = () => {
            previousApiLoadCallback();
            resolve();
        };
    }
    else {
        localWindow.onYouTubeIframeAPIReady = () => {
            resolve();
        };
    }
});
const addVideoStartedClass = (el) => {
    if (el) {
        el.classList.add('youtube__video-started');
    }
};
let resolveInitialConsent;
const initialConsent = new Promise((resolve) => {
    // We don’t need to wait for consent if Ad-Free
    if (commercialFeatures.adFree) {
        resolve({
            canTarget: false,
            framework: null,
        });
    }
    resolveInitialConsent = resolve;
});
onConsentChange((consentState) => {
    resolveInitialConsent(consentState);
});
const playerStates = [
    'ENDED',
    'PLAYING',
    'PAUSED',
    'BUFFERING',
    'CUED',
];
const onPlayerStateChangeEvent = (event, handlers, el) => {
    if (el) {
        if (config.get('page.isDev')) {
            const playerState = window.YT.PlayerState[event.data];
            if (playerState) {
                console.log(`Player ${el.id} is ${playerState}`);
            }
        }
        // change class according to the current state
        // TODO: Fix this so we can add poster image.
        fastdom.mutate(() => {
            playerStates.forEach((status) => {
                el.classList.toggle(`youtube__video-${status.toLocaleLowerCase()}`, event.data === window.YT.PlayerState[status]);
                const fcItem = el.closest('fc-item');
                if (fcItem) {
                    fcItem.classList.toggle(`fc-item--has-video-main-media__${status.toLocaleLowerCase()}`, event.data === window.YT.PlayerState[status]);
                }
                addVideoStartedClass(el);
            });
        });
    }
    handlers?.onPlayerStateChange(event);
};
const onPlayerReadyEvent = (event, handlers, el) => {
    fastdom.mutate(() => {
        if (el) {
            el.classList.add('youtube__video-ready');
            const fcItem = el.closest('fc-item');
            if (fcItem) {
                fcItem.classList.add('fc-item--has-video-main-media__ready');
            }
        }
    });
    handlers?.onPlayerReady(event);
};
const createAdsConfigDisabled = () => {
    const adFreeConfig = { disableAds: true };
    log('commercial', 'YouTube Ad-Free Config', adFreeConfig);
    return adFreeConfig;
};
/**
 * TODO: Use buildAdsConfig from `@guardian/commercial`
 * @param consentState
 * @returns A valid YouTube ads config
 */
const createAdsConfigEnabled = (consentState) => {
    const custParams = getPageTargeting(consentState);
    custParams.permutive = getPermutivePFPSegments();
    const adsConfigBasic = {
        adTagParameters: {
            iu: config.get('page.adUnit', ''),
            cust_params: encodeURIComponent(constructQuery(custParams)),
        },
    };
    if (consentState.tcfv2) {
        const adsConfigTCFv2 = {
            adTagParameters: {
                ...adsConfigBasic.adTagParameters,
                cmpGdpr: consentState.tcfv2.gdprApplies ? 1 : 0,
                cmpVcd: consentState.tcfv2.tcString,
                cmpGvcd: consentState.tcfv2.addtlConsent,
            },
            nonPersonalizedAd: !consentState.canTarget,
        };
        log('commercial', 'YouTube Ads Config TCFv2', adsConfigTCFv2);
        return adsConfigTCFv2;
    }
    if (consentState.usnat || consentState.aus) {
        const adsConfigUSNAT = {
            ...adsConfigBasic,
            restrictedDataProcessor: !consentState.canTarget,
        };
        log('commercial', 'YouTube Ads Config CCPA/AUS', adsConfigUSNAT);
        return adsConfigUSNAT;
    }
    return adsConfigBasic;
};
const getHost = ({ consentState, classes, adFree, }) => {
    if (consentState.canTarget &&
        !adFree &&
        classes.includes('youtube-media-atom__iframe')) {
        return 'https://www.youtube.com';
    }
    // Default to no-cookie
    return 'https://www.youtube-nocookie.com';
};
const setupPlayer = (el, videoId, onReady, onStateChange, onError, consentState) => {
    // relatedChannels needs to be an array, as per YouTube's IFrame Embed Config API
    const relatedChannels = [];
    /**
     * There's an issue with relatedChannels where
     * if we pass a populated array no related videos are
     * shown. Therefore for the time being we will pass an
     * empty array.
     */
    const adsConfig = !commercialFeatures.youtubeAdvertising
        ? createAdsConfigDisabled()
        : createAdsConfigEnabled(consentState);
    /**
     * Note:
     * This element id must be unique!
     * Ensured via the SSR render of youtube.scala.html
     */
    // @ts-expect-error -- ts is confused by multiple constructors
    return new window.YT.Player(el.id, {
        host: getHost({
            consentState,
            classes: [...el.classList.values()],
            adFree: commercialFeatures.adFree,
        }),
        videoId,
        width: '100%',
        height: '100%',
        events: {
            onReady,
            onStateChange,
            onError,
        },
        embedConfig: {
            relatedChannels,
            adsConfig,
        },
    });
};
const hasPlayerStarted = (event) => event.target.getCurrentTime() > 0;
const getPlayerIframe = (id) => document.getElementById(id);
export const initYoutubePlayer = async (el, handlers, videoId) => {
    await loadScript(scriptSrc, {});
    await youtubeReady;
    const consentState = await initialConsent;
    const onPlayerStateChange = (event) => {
        onPlayerStateChangeEvent(event, handlers, getPlayerIframe(el.id));
    };
    const onPlayerReady = (event) => {
        const iframe = getPlayerIframe(el.id);
        if (hasPlayerStarted(event)) {
            addVideoStartedClass(iframe);
        }
        onPlayerReadyEvent(event, handlers, iframe);
    };
    const onPlayerError = (event) => {
        console.error(`YOUTUBE: ${event.data}`);
        console.dir(event);
    };
    return setupPlayer(el, videoId, onPlayerReady, onPlayerStateChange, onPlayerError, consentState);
};
export const _ = {
    createAdsConfig: createAdsConfigEnabled,
    createAdFreeConfig: createAdsConfigDisabled,
    getHost,
};
